/**
 * liveSocket.ts — Org canlı izleme WebSocket istemcisi.
 *
 * Backend: WS /ws/org/live?token=<ws_token>
 * ws_token: GET /api/org/auth/ws-token (kısa ömürlü, 60s).
 * Mobil `mobile/src/services/websocket.ts` deseni: query token + otomatik
 * yeniden bağlanma. EventSource Bearer sorununu kökten çözer.
 *
 * Gelen JSON: { type: 'LIVE_CALLS'|'CALL_UPDATE'|'CALL_ENDED'|'ping', ... }
 */

import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';

export interface LiveMessage {
  type: 'LIVE_CALLS' | 'CALL_UPDATE' | 'CALL_ENDED' | 'ping';
  calls?: unknown[];
  call?: unknown;
  ts?: number;
}

interface Handlers {
  onMessage: (msg: LiveMessage) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

function wsBaseUrl(): string {
  // API.baseURL → ws(s):// karşılığı
  const base = API.baseURL.replace(/^http/, 'ws');
  return `${base}/ws/org/live`;
}

export class LiveSocket {
  private ws: WebSocket | null = null;
  private closedByUser = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private handlers: Handlers) {}

  async connect(): Promise<void> {
    this.closedByUser = false;
    let token: string;
    try {
      const { data } = await apiClient.get<{ ws_token: string }>(API.org.wsToken);
      token = data.ws_token;
    } catch {
      this.scheduleReconnect();
      return;
    }

    const ws = new WebSocket(`${wsBaseUrl()}?token=${encodeURIComponent(token)}`);
    this.ws = ws;

    ws.onopen = () => this.handlers.onOpen?.();
    ws.onmessage = (e) => {
      try {
        this.handlers.onMessage(JSON.parse(e.data) as LiveMessage);
      } catch {
        /* yok say */
      }
    };
    ws.onclose = () => {
      this.handlers.onClose?.();
      if (!this.closedByUser) this.scheduleReconnect();
    };
    ws.onerror = () => ws.close();
  }

  private scheduleReconnect() {
    if (this.closedByUser || this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, 5000);
  }

  close() {
    this.closedByUser = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
  }
}
